import { defineStore } from 'pinia'
import { usageService } from '@/services/usage'
import { errorMessage } from '@/services/http'
import { downloadBlob } from '@/utils/download'
import { createLatest } from '@/utils/latest'
import type { LedgerEntry, UsageCursor, UsageFilter } from '@/types/api'

const EXPORT_FILENAME = 'casualwerks-usage.xlsx'

const latest = createLatest()

interface UsageState {
  entries: LedgerEntry[]
  totalCredits: number
  hasMore: boolean
  loading: boolean
  loadingMore: boolean
  exporting: boolean
  error: string | null
}

export const useUsageStore = defineStore('usage', {
  state: (): UsageState => ({
    entries: [],
    totalCredits: 0,
    hasMore: false,
    loading: false,
    loadingMore: false,
    exporting: false,
    error: null,
  }),

  getters: {
    cursor: (state): UsageCursor | null => {
      const last = state.entries[state.entries.length - 1]
      return last ? { cursor_date: last.created_date, cursor_id: last.id } : null
    },
  },

  actions: {
    async fetch(filter: UsageFilter): Promise<void> {
      const isCurrent = latest.claim()
      this.loading = true
      this.error = null
      try {
        const page = await usageService.list(filter, null)
        if (!isCurrent()) return
        this.entries = page.items
        this.totalCredits = page.total_credits
        this.hasMore = page.has_more
      } catch (e) {
        if (!isCurrent()) return
        this.error = errorMessage(e)
        this.entries = []
        this.totalCredits = 0
        this.hasMore = false
      } finally {
        if (isCurrent()) this.loading = false
      }
    },

    async fetchMore(filter: UsageFilter): Promise<void> {
      if (this.loading || this.loadingMore || !this.hasMore) return
      const isCurrent = latest.observe()
      this.loadingMore = true
      this.error = null
      try {
        const page = await usageService.list(filter, this.cursor)
        if (!isCurrent()) return
        this.entries = this.entries.concat(page.items)
        this.hasMore = page.has_more
      } catch (e) {
        if (isCurrent()) this.error = errorMessage(e)
      } finally {
        this.loadingMore = false
      }
    },

    async exportXlsx(filter: UsageFilter): Promise<void> {
      if (this.exporting) return
      this.exporting = true
      this.error = null
      try {
        downloadBlob(await usageService.exportXlsx(filter), EXPORT_FILENAME)
      } catch (e) {
        this.error = errorMessage(e)
      } finally {
        this.exporting = false
      }
    },
  },
})
